import { useEffect, useState } from "react";
import { Plus, Pencil, Archive, RotateCcw, X, Trash2, Layers, ListChecks } from "lucide-react";
import {
  useProjectTypeStore,
  type ProjectTypeConfig,
  type ChecklistItem,
  type ProjectSubType,
} from "../store/useProjectTypeStore";

const blankItem = (): ChecklistItem => ({ label: "", required: true });

// ----- Checklist editor (shared by type-level and sub-type-level) -----
function ChecklistEditor({
  items,
  onChange,
}: {
  items: ChecklistItem[];
  onChange: (items: ChecklistItem[]) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={it.label}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
            placeholder="Requirement (e.g. Hosting Access)"
            className="flex-1 px-3 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary"
          />
          <label className="flex items-center gap-1 text-[11px] text-on-surface-variant whitespace-nowrap">
            <input
              type="checkbox"
              checked={it.required !== false}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))}
            />
            Required
          </label>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-1 text-outline hover:text-error">
            <Trash2 size={15} />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...items, blankItem()])}
        className="self-start text-primary text-xs font-semibold hover:underline flex items-center gap-1"
      >
        <Plus size={13} /> Add checklist item
      </button>
    </div>
  );
}

// ----- Type editor modal -----
function TypeEditor({
  initial,
  onClose,
  onSave,
}: {
  initial: Partial<ProjectTypeConfig>;
  onClose: () => void;
  onSave: (body: Partial<ProjectTypeConfig>) => Promise<boolean>;
}) {
  const [name, setName] = useState(initial.name || "");
  const [order, setOrder] = useState(initial.order ?? 0);
  const [checklist, setChecklist] = useState<ChecklistItem[]>(initial.checklist || []);
  const [subTypes, setSubTypes] = useState<ProjectSubType[]>(initial.subTypes || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const ok = await onSave({
      name: name.trim(),
      order: Number(order) || 0,
      checklist: checklist.filter((c) => c.label.trim()),
      subTypes: subTypes
        .filter((s) => s.name.trim())
        .map((s) => ({ ...s, checklist: (s.checklist || []).filter((c) => c.label.trim()) })),
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-surface-container-lowest w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant">
          <h2 className="text-lg font-bold text-on-surface flex items-center gap-2">
            <Layers size={18} className="text-primary" /> {initial._id ? "Edit" : "New"} Project Type
          </h2>
          <button onClick={onClose} className="p-1.5 text-outline hover:bg-surface-container rounded-full">
            <X size={18} />
          </button>
        </div>

        <div className="cre-scroll overflow-y-auto flex-1 p-6 flex flex-col gap-5">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-[11px] font-semibold text-outline uppercase">Type Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Website Development"
                className="w-full mt-1 px-3 py-2 bg-surface-container-low border border-outline-variant rounded-lg text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="w-24">
              <label className="text-[11px] font-semibold text-outline uppercase">Order</label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 bg-surface-container-low border border-outline-variant rounded-lg text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-on-surface flex items-center gap-1.5 mb-2">
              <ListChecks size={15} className="text-primary" /> Type-level Requirement Checklist
            </h4>
            <ChecklistEditor items={checklist} onChange={setChecklist} />
          </div>

          <div className="border-t border-outline-variant pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-on-surface">Sub-Types</h4>
              <button
                onClick={() => setSubTypes([...subTypes, { name: "", active: true, checklist: [] }])}
                className="text-primary text-xs font-semibold hover:underline flex items-center gap-1"
              >
                <Plus size={13} /> Add sub-type
              </button>
            </div>
            <div className="flex flex-col gap-4">
              {subTypes.map((st, i) => (
                <div key={i} className="border border-outline-variant rounded-lg p-3 bg-surface-bright">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      value={st.name}
                      onChange={(e) => setSubTypes(subTypes.map((s, j) => (j === i ? { ...s, name: e.target.value } : s)))}
                      placeholder="Sub-type (e.g. Logo Design)"
                      className="flex-1 px-3 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary"
                    />
                    <label className="flex items-center gap-1 text-[11px] text-on-surface-variant">
                      <input
                        type="checkbox"
                        checked={st.active}
                        onChange={(e) => setSubTypes(subTypes.map((s, j) => (j === i ? { ...s, active: e.target.checked } : s)))}
                      />
                      Active
                    </label>
                    <button onClick={() => setSubTypes(subTypes.filter((_, j) => j !== i))} className="p-1 text-outline hover:text-error">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="pl-1">
                    <span className="text-[11px] text-outline uppercase">Checklist</span>
                    <div className="mt-1">
                      <ChecklistEditor
                        items={st.checklist || []}
                        onChange={(items) => setSubTypes(subTypes.map((s, j) => (j === i ? { ...s, checklist: items } : s)))}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {subTypes.length === 0 && <p className="text-xs text-outline">No sub-types. Add one if this type needs sub-categories.</p>}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-outline-variant">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-on-surface-variant hover:bg-surface-container rounded-lg">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-[#004b9e] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProjectTypesPage() {
  const { types, fetchTypes, createType, updateType, setActive } = useProjectTypeStore();
  const [editing, setEditing] = useState<Partial<ProjectTypeConfig> | null>(null);

  useEffect(() => {
    fetchTypes(true);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1100px] mx-auto px-4 lg:px-8 pt-20 pb-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-[26px] font-bold text-on-surface tracking-tight">Project Types</h1>
            <p className="text-[14px] text-on-surface-variant mt-1">
              Configure project types, sub-types and requirement checklists. These populate Project Creation and Cost Configuration.
            </p>
          </div>
          <button
            onClick={() => setEditing({})}
            className="bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-[#004b9e]"
          >
            <Plus size={18} /> Add Type
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {types.map((t) => (
            <div
              key={t._id}
              className={`bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-4 ${t.active ? "" : "opacity-60"}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-on-surface flex items-center gap-2">
                    {t.name}
                    {!t.active && <span className="text-[10px] font-bold uppercase bg-surface-container text-on-surface-variant px-2 py-0.5 rounded-full">Archived</span>}
                  </h3>
                  <p className="text-[11px] text-on-surface-variant mt-0.5">
                    {t.checklist.length} checklist item{t.checklist.length !== 1 ? "s" : ""} · {t.subTypes.length} sub-type{t.subTypes.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(t)} className="p-1.5 text-outline hover:text-primary hover:bg-surface-container rounded-lg" title="Edit">
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => setActive(t._id, !t.active)}
                    className="p-1.5 text-outline hover:text-primary hover:bg-surface-container rounded-lg"
                    title={t.active ? "Archive" : "Activate"}
                  >
                    {t.active ? <Archive size={16} /> : <RotateCcw size={16} />}
                  </button>
                </div>
              </div>
              {t.subTypes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {t.subTypes.map((s, i) => (
                    <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full border ${s.active ? "bg-surface-container-low text-on-surface border-outline-variant" : "bg-surface-container text-outline border-outline-variant line-through"}`}>
                      {s.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {types.length === 0 && <div className="text-center py-16 text-outline">No project types yet. Add one to get started.</div>}
      </div>

      {editing && (
        <TypeEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={(body) => (editing._id ? updateType(editing._id, body) : createType(body))}
        />
      )}
    </div>
  );
}
